import { useRef, useState } from "react";
import { toast } from "../../utils/toast";
import { Button, InfoBox, Input, Modal, RadioGroup, Textarea } from "../ui";
import { errorMessageOf, problemOf, scholarshipsApi } from "../../api/scholarships";
import { VISIBILITIES, closesAtOf, formatKigaliDate, kindOf, todayInKigali, useScholarshipChange } from "./model";
import EvidenceCard, { EVIDENCE } from "./EvidenceCard";

const MIN_REASON = 10;
const MAX_REASON = 1000;
const visibilityLabel = (value) => VISIBILITIES.find((v) => v.value === value)?.label;

const problemsOf = ({ reason, visibility, closingDate }) => {
  const trimmed = reason.trim();
  return {
    reason: !trimmed
      ? "Say why you are reopening it."
      : trimmed.length < MIN_REASON || reason.length > MAX_REASON
        ? `Give a reason of ${MIN_REASON} to ${MAX_REASON} characters.`
        : null,
    visibility: visibility ? null : "Choose who can see it once it is open.",
    closingDate: !closingDate
      ? "Choose the new closing date."
      : new Date(closesAtOf(closingDate)) <= new Date()
        ? "Choose a closing date in the future."
        : null,
  };
};

/**
 * Reopens a closed scholarship: the Admin gives a reason, chooses Public or Private (no default) and a
 * new closing date, then confirms a one-line summary. Applications, marks and comments are kept.
 */
export default function ReopenDialog({ scholarship, open, onClose }) {
  const { one } = kindOf(scholarship.type);
  const change = useScholarshipChange(scholarship.id);
  const [form, setForm] = useState({ reason: "", visibility: "", closingDate: "" });
  const [shown, setShown] = useState({}); // fields whose errors are shown: touched, or after Continue
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const refs = { reason: useRef(null), closingDate: useRef(null) };

  const problems = problemsOf(form);
  const errorOf = (field) => (shown[field] ? problems[field] : null) ?? undefined;
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const touch = (field) => () => setShown((s) => ({ ...s, [field]: true }));

  const close = () => {
    if (busy) return;
    setForm({ reason: "", visibility: "", closingDate: "" });
    setShown({});
    setConfirming(false);
    onClose();
  };

  const next = () => {
    setShown({ reason: true, visibility: true, closingDate: true });
    const first = ["reason", "visibility", "closingDate"].find((f) => problems[f]);
    if (!first) return setConfirming(true);
    if (first === "visibility") document.querySelector(`input[name="reopen-visibility"]`)?.focus();
    else refs[first].current?.focus();
  };

  const reopen = async () => {
    setBusy(true);
    try {
      const body = { reason: form.reason.trim(), visibility: form.visibility, closesAt: closesAtOf(form.closingDate) };
      await change((etag) => scholarshipsApi.post(`admin/scholarships/${scholarship.id}/reopen`, body, { ifMatch: etag }));
      toast.success(`Reopened as ${visibilityLabel(form.visibility)} until ${formatKigaliDate(body.closesAt)}`);
      setBusy(false);
      close();
    } catch (error) {
      console.error(`Failed to reopen scholarship ${scholarship.id}:`, error);
      setBusy(false);
      // A date that passed while the dialog was open goes back to the form
      if (problemOf(error)?.code === "closing-date-past") setConfirming(false);
      toast.error(errorMessageOf(error, `The ${one} was not reopened. Please try again.`));
    }
  };

  const until = formatKigaliDate(form.closingDate && closesAtOf(form.closingDate));
  const footer = confirming ? (
    <>
      <Button variant="ghost" size="sm" onClick={() => setConfirming(false)} disabled={busy}>
        Back
      </Button>
      <Button size="sm" onClick={reopen} loading={busy}>
        Reopen
      </Button>
    </>
  ) : (
    <>
      <Button variant="ghost" size="sm" onClick={close}>
        Cancel
      </Button>
      <Button size="sm" onClick={next}>
        Continue
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={close}
      dismissible={!busy}
      title={confirming ? `Reopen as ${visibilityLabel(form.visibility)} until ${until}?` : "Reopen applications"}
      description={confirming ? null : `${scholarship.name} is closed. Reopening lets students apply again until the new closing date.`}
      footer={footer}
    >
      {confirming ? (
        <div className="flex flex-col gap-3 text-sm text-slate-700">
          <ul className="list-disc space-y-1 pl-5">
            <li>Applications, marks and comments already made are kept.</li>
            {form.visibility === "PUBLIC" ? (
              <li>The {one} is listed for students again, and students who had not submitted can finish their drafts.</li>
            ) : (
              <li>Only invited students can see and apply; invitations not sent yet go out. Drafts that were not submitted stay not submitted.</li>
            )}
            <li>If an award list was finalised and no student has been told a result, it goes back to a draft.</li>
          </ul>
          <p className="rounded-md bg-slate-50 px-3 py-2">
            <span className="font-medium">Reason:</span> {form.reason.trim()}
          </p>
        </div>
      ) : (
        <form
          className="flex flex-col gap-5"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            next();
          }}
        >
          <Textarea
            ref={refs.reason}
            label="Reason"
            required
            rows={3}
            maxLength={MAX_REASON}
            value={form.reason}
            onChange={set("reason")}
            onBlur={touch("reason")}
            info={`${form.reason.length} of ${MAX_REASON} characters. Kept in the ${one}'s history.`}
            error={errorOf("reason")}
          />
          <RadioGroup
            label="Who can see it once it is open"
            name="reopen-visibility"
            required
            value={form.visibility}
            options={VISIBILITIES}
            onChange={(e) => {
              set("visibility")(e);
              touch("visibility")();
            }}
            info="Public: listed for every student. Private: only students with the link can apply."
            error={errorOf("visibility")}
          />
          {/* Reopening as private needs the reason uploaded first */}
          {form.visibility === "PRIVATE" && <EvidenceCard scholarship={scholarship} kind={EVIDENCE.privacy} reopening />}
          <Input
            ref={refs.closingDate}
            type="date"
            label="New closing date"
            required
            min={todayInKigali()}
            value={form.closingDate}
            onChange={set("closingDate")}
            onBlur={touch("closingDate")}
            info="Applications close at 23:59, Kigali time."
            error={errorOf("closingDate")}
          />
          {scholarship.visibility && (
            <InfoBox>It was {visibilityLabel(scholarship.visibility)} and closed on {formatKigaliDate(scholarship.closedAt ?? scholarship.closesAt)}.</InfoBox>
          )}
        </form>
      )}
    </Modal>
  );
}
